import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, fmtDate, errDetail } from "../lib/api";
import { useApp } from "../lib/store";
import { DataTable } from "../components/DataTable";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { trackRecent } from "../components/Layout";
import {
  Map, ChevronUp, Plus, KanbanSquare, CalendarRange, List, Rocket, Download, Sparkles, SlidersHorizontal,
} from "lucide-react";

const STATUSES = ["Under consideration", "Planned", "In development", "In testing", "Released"];
const TERMINAL = ["Declined", "Parked"];
const TYPES = ["New feature", "Improvement", "Integration", "Performance", "Compliance"];
const CATEGORIES = [
  "Orders", "AI Order Intake", "ERP / Interfaces", "Deliveries & Logistics", "Article Master",
  "Conditions & Pricing", "Tax & Compliance", "Finished Goods Stock", "AI Cost Control",
  "Dashboard", "Accounts", "Opportunities", "Quotes", "Complaints", "Reports", "Admin", "Other",
];
const STATUS_COLORS = {
  "Under consideration": "bg-slate-100 text-slate-600", Planned: "bg-indigo-50 text-indigo-700",
  "In development": "bg-amber-50 text-amber-700", "In testing": "bg-blue-50 text-blue-700",
  Released: "bg-emerald-100 text-emerald-800", Declined: "bg-red-50 text-red-600", Parked: "bg-slate-200 text-slate-500",
};
const TYPE_COLORS = {
  "New feature": "bg-emerald-50 text-emerald-700", Improvement: "bg-blue-50 text-blue-700",
  Integration: "bg-violet-50 text-violet-700", Performance: "bg-amber-50 text-amber-700",
  Compliance: "bg-rose-50 text-rose-700",
};

function VoteButton({ item, onVote, t }) {
  return (
    <button onClick={(e) => { e.stopPropagation(); onVote(item); }} data-testid={`roadmap-vote-${item.id}`}
      className={`flex flex-col items-center rounded-lg border px-2.5 py-1.5 shrink-0 transition-colors duration-150 ${
        item.voted ? "border-[#F5A623] bg-amber-50 text-[#1B2A4A]" : "border-gray-200 text-gray-500 hover:border-[#F5A623]"}`}>
      <ChevronUp className={`h-4 w-4 ${item.voted ? "text-[#F5A623]" : ""}`} strokeWidth={2.5} />
      <span className="text-sm font-bold font-mono">{item.votes || 0}</span>
    </button>
  );
}

function Card({ item, onVote, onOpen, t }) {
  const inDev = item.status === "In development";
  return (
    <div onClick={() => onOpen(item)} data-testid={`roadmap-card-${item.id}`}
      className="bg-white border border-gray-200 rounded-lg p-3 mb-2.5 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-150">
      <div className="flex items-start gap-3">
        <VoteButton item={item} onVote={onVote} t={t} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
            <span className="font-mono text-[10px] text-gray-400">{item.item_number}</span>
            <Badge className={`${TYPE_COLORS[item.type] || ""} hover:bg-inherit text-[10px]`}>{item.type}</Badge>
          </div>
          <p className="text-sm font-semibold text-[#1B2A4A] leading-snug">{item.title}</p>
          <div className="flex items-center gap-2 mt-1.5 text-[11px] text-gray-500 flex-wrap">
            <span>{item.category}</span>
            {item.target_quarter && <><span>·</span><span className="font-medium">{item.target_quarter}</span></>}
          </div>
          {inDev && (
            <div className="mt-2 h-1.5 rounded-full bg-gray-100 overflow-hidden">
              <div className="h-full bg-[#F5A623] rounded-full" style={{ width: "55%" }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Roadmap() {
  const { t, canWrite, isManager, user } = useApp();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [budget, setBudget] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [budgetVal, setBudgetVal] = useState(10);
  const [form, setForm] = useState({ title: "", description: "", business_value: "", category: "Orders", type: "New feature" });

  const load = useCallback(() => {
    api.get("/roadmap").then((r) => setRows(r.data)).catch(() => {});
    api.get("/roadmap/meta").then((r) => setBudget(r.data.budget)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    try { await api.put("/roadmap/settings", { vote_budget: Number(budgetVal) }); setSettingsOpen(false); toast.success(t("save") + " ✓"); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };

  const open = (r) => { trackRecent(r.item_number, `/roadmap/${r.id}`); navigate(`/roadmap/${r.id}`); };

  const vote = async (item) => {
    try {
      const r = await api.post(`/roadmap/${item.id}/vote`);
      setBudget(r.data.budget);
      setRows((p) => p.map((x) => x.id === item.id ? { ...x, votes: r.data.votes, voted: r.data.voted } : x));
      if (r.data.voted) toast.success(t("roadmap_voted"));
    } catch (e) {
      const status = e?.response?.status;
      if (status === 429) toast.error(errDetail(e));
      else toast.error(errDetail(e));
    }
  };

  const submit = async () => {
    try {
      const r = await api.post("/roadmap", form);
      setDialogOpen(false);
      setForm({ title: "", description: "", business_value: "", category: "Orders", type: "New feature" });
      toast.success(t("roadmap_submitted"));
      navigate(`/roadmap/${r.data.id}`);
    } catch (e) { toast.error(errDetail(e)); }
  };

  const downloadDigest = () => {
    const url = `${API}/roadmap/digest-pdf?auth=${localStorage.getItem("lf_token")}`;
    window.open(url, "_blank");
  };

  const released = useMemo(() => rows.filter((r) => r.status === "Released")
    .sort((a, b) => (b.released_at || "").localeCompare(a.released_at || "")), [rows]);

  const byStatus = (s) => rows.filter((r) => r.status === s).sort((a, b) => (b.votes || 0) - (a.votes || 0));

  const quarters = useMemo(() => {
    const set = new Set(rows.map((r) => r.target_quarter).filter(Boolean));
    return Array.from(set).sort();
  }, [rows]);

  const listColumns = [
    { key: "item_number", label: "#", render: (r) => <span className="font-mono text-[#1B2A4A]">{r.item_number}</span> },
    { key: "title", label: t("roadmap_title_field"), render: (r) => <span className="font-medium">{r.title}</span> },
    { key: "category", label: t("roadmap_category") },
    { key: "type", label: t("type"), render: (r) => <Badge className={`${TYPE_COLORS[r.type] || ""} hover:bg-inherit`}>{r.type}</Badge> },
    { key: "status", label: t("status"), render: (r) => <Badge className={`${STATUS_COLORS[r.status] || ""} hover:bg-inherit`}>{r.status}</Badge> },
    { key: "target_quarter", label: t("roadmap_quarter"), render: (r) => r.target_quarter || "–" },
    { key: "votes", label: t("ib_votes"), align: "right", render: (r) => (
      <span className="inline-flex items-center gap-1 font-mono font-semibold text-[#1B2A4A]"><ChevronUp className="h-3.5 w-3.5 text-[#F5A623]" />{r.votes || 0}</span>) },
  ];

  return (
    <div className="space-y-4" data-testid="roadmap-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900" style={{ fontFamily: "Work Sans" }}>{t("roadmap")}</h1>
          <p className="text-sm text-gray-500 mt-1">{t("roadmap_desc")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {budget && (
            <Badge className="bg-[#1B2A4A] text-white hover:bg-[#1B2A4A]" data-testid="roadmap-budget-badge">
              {t("roadmap_votes_left")}: {budget.remaining}/{budget.budget}
            </Badge>
          )}
          {isManager && (
            <Button size="sm" variant="outline" onClick={downloadDigest} data-testid="roadmap-digest-btn" className="rounded-full">
              <Download className="h-3.5 w-3.5 mr-1" />{t("roadmap_digest")}
            </Button>
          )}
          {user?.role === "admin" && (
            <Button size="sm" variant="outline" onClick={() => { setBudgetVal(budget?.budget || 10); setSettingsOpen(true); }} data-testid="roadmap-settings-btn" className="rounded-full">
              <SlidersHorizontal className="h-3.5 w-3.5 mr-1" />{t("roadmap_vote_budget")}
            </Button>
          )}
          {canWrite && (
            <Button size="sm" onClick={() => setDialogOpen(true)} data-testid="roadmap-new-btn"
              className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold rounded-full">
              <Plus className="h-4 w-4 mr-1" strokeWidth={2.5} />{t("roadmap_submit")}
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="kanban">
        <TabsList className="bg-white border border-gray-200">
          <TabsTrigger value="kanban" data-testid="roadmap-tab-kanban"><KanbanSquare className="h-3.5 w-3.5 mr-1.5" />{t("roadmap_kanban")}</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="roadmap-tab-timeline"><CalendarRange className="h-3.5 w-3.5 mr-1.5" />{t("roadmap_timeline")}</TabsTrigger>
          <TabsTrigger value="list" data-testid="roadmap-tab-list"><List className="h-3.5 w-3.5 mr-1.5" />{t("roadmap_list")}</TabsTrigger>
        </TabsList>

        {/* Kanban */}
        <TabsContent value="kanban" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="roadmap-kanban">
            {STATUSES.map((s) => {
              const items = byStatus(s);
              return (
                <div key={s} className="bg-[#F4F5F7] rounded-lg p-2.5" data-testid={`roadmap-col-${s.replace(/\s+/g, "-").toLowerCase()}`}>
                  <div className="flex items-center justify-between px-1 mb-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">{s}</span>
                    <span className="text-[10px] font-mono text-gray-400">{items.length}</span>
                  </div>
                  {items.map((it) => <Card key={it.id} item={it} onVote={vote} onOpen={open} t={t} />)}
                  {items.length === 0 && <p className="text-[11px] text-gray-400 text-center py-4">–</p>}
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* Timeline by quarter */}
        <TabsContent value="timeline" className="mt-4">
          <div className="space-y-5" data-testid="roadmap-timeline">
            {[...quarters, "__none__"].map((qk) => {
              const items = rows.filter((r) => (qk === "__none__" ? !r.target_quarter : r.target_quarter === qk) && r.status !== "Released");
              if (items.length === 0) return null;
              const byModule = {};
              items.forEach((it) => { (byModule[it.category] = byModule[it.category] || []).push(it); });
              return (
                <div key={qk} className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-bold text-[#1B2A4A] mb-3 flex items-center gap-2">
                    <CalendarRange className="h-4 w-4 text-[#F5A623]" />{qk === "__none__" ? t("roadmap_unscheduled") : qk}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
                    {Object.entries(byModule).map(([mod, its]) => (
                      <div key={mod} className="mb-2">
                        <div className="text-[10px] uppercase tracking-widest text-gray-400 mb-1">{mod}</div>
                        {its.map((it) => <Card key={it.id} item={it} onVote={vote} onOpen={open} t={t} />)}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* List */}
        <TabsContent value="list" className="mt-4">
          <DataTable module="roadmap" rows={rows} columns={listColumns} onRowClick={open}
            filters={[
              { key: "status", label: t("status"), options: [...STATUSES, ...TERMINAL] },
              { key: "type", label: t("type"), options: TYPES },
              { key: "category", label: t("roadmap_category"), options: CATEGORIES },
            ]} />
        </TabsContent>
      </Tabs>

      {/* Recently released */}
      {released.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="roadmap-released">
          <h2 className="text-sm font-bold text-[#1B2A4A] mb-3 flex items-center gap-2">
            <Rocket className="h-4 w-4 text-emerald-600" />{t("roadmap_recently_released")}
          </h2>
          <div className="space-y-2">
            {released.map((r) => (
              <button key={r.id} onClick={() => open(r)} data-testid={`roadmap-released-${r.id}`}
                className="w-full text-left flex items-start gap-3 p-2 rounded-md hover:bg-emerald-50/50 transition-colors">
                <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 shrink-0">{r.release_version || "✓"}</Badge>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#1B2A4A]">{r.title}</p>
                  {r.release_note && <p className="text-xs text-gray-500">{r.release_note}</p>}
                  <p className="text-[10px] text-gray-400 mt-0.5">{fmtDate(r.released_at)}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Submit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#F5A623]" />{t("roadmap_submit")}</DialogTitle>
            <DialogDescription>{t("roadmap_submit_desc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">{t("roadmap_title_field")}</Label>
              <Input data-testid="roadmap-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("ib_description")}</Label>
              <Textarea data-testid="roadmap-form-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("roadmap_business_value")}</Label>
              <Input data-testid="roadmap-form-value" value={form.business_value} onChange={(e) => setForm({ ...form, business_value: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">{t("roadmap_category")}</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="roadmap-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t("type")}</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="roadmap-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t("cancel")}</Button>
            <Button onClick={submit} data-testid="roadmap-form-submit" className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold">{t("roadmap_submit")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin: vote budget settings */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t("roadmap_vote_budget")}</DialogTitle>
            <DialogDescription>{t("roadmap_submit_desc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label className="text-xs">{t("roadmap_vote_budget")}</Label>
            <Input type="number" min={1} max={100} value={budgetVal} onChange={(e) => setBudgetVal(e.target.value)} data-testid="roadmap-budget-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>{t("cancel")}</Button>
            <Button onClick={saveSettings} data-testid="roadmap-budget-save" className="bg-[#1B2A4A]">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
